import Promise from "bluebird";
import map from "lodash/map";
import invariant from "invariant";

import firebaseApp from "../config/firebase";
import { handleException } from "../app";
import { TIMELINE_ACTION_TYPES } from "./constants";
import { getActiveProject } from "../projects";
import { postRequest } from "../../RequestUtil";

import type { Dispatch, GetState } from "../types";
import type {
  CreateTimelineEventObject,
  UpdateTimelineEventObject,
  DeleteTimelineEventObject,
} from "./types";
import type TimelineEvent from "../../models/TimelineEvent";

import { SEND_SCHEDULE_CHANGE_URL } from "./constants";

export function createTimelineEvent(
  params: CreateTimelineEventObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.CREATE_TIMELINE_EVENT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const event = {
        name: params.name,
        dateTime: params.dateTime,
        location: params.location,
        details: params.details,
        projectId: params.projectId,
        createdBy: currentUser.uid,
      };
      const eventRef = await db.ref("timelineEvents").push(event);
      dispatch({
        type: TIMELINE_ACTION_TYPES.RECEIVE_TIMELINE_EVENT,
        payload: {
          timelineEvent: {
            uid: eventRef.key,
            ...event,
          },
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function updateTimelineEvent(
  params: UpdateTimelineEventObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.UPDATE_TIMELINE_EVENT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const event = {
        name: params.name,
        dateTime: params.dateTime,
        location: params.location,
        details: params.details,
        projectId: params.projectId,
        createdBy: currentUser.uid,
      };
      await db.ref(`timelineEvents/${params.uid}`).set(event);
      const value = await db.ref(`timelineEvents/${params.uid}`).once("value");
      await postRequest({
        url: SEND_SCHEDULE_CHANGE_URL,
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          project: params.projectId,
          user: currentUser.uid,
        },
      });
      dispatch({
        type: TIMELINE_ACTION_TYPES.RECEIVE_UPDATED_EVENT,
        payload: {
          timelineEvent: value.toJSON(),
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteTimelineEvent(
  params: DeleteTimelineEventObject
): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.DELETE_TIMELINE_EVENT,
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      await db.ref(`timelineEvents/${params.uid}`).set(null);
      dispatch({
        type: TIMELINE_ACTION_TYPES.RECEIVE_DELETED_EVENT,
        payload: {
          timelineEventUid: params.uid,
        },
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadTimelineEvents(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.FETCH_TIMELINE_EVENTS,
    });
    try {
      const activeProject = getActiveProject(getState());
      invariant(
        activeProject,
        "Cannot load timeline without an active project."
      );
      const timelineEventsRef = firebaseApp
        .database()
        .ref("timelineEvents")
        .orderByChild("projectId")
        .equalTo(activeProject.id);
      await timelineEventsRef.on("value", (timelineEvents) => {
        dispatch({
          type: TIMELINE_ACTION_TYPES.RECEIVE_TIMELINE_EVENTS,
          payload: {
            timelineEvents:
              map(timelineEvents.toJSON(), (event, key) => ({
                uid: key,
                ...event,
              })) || [],
          },
        });
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadDefaultTimelineEvents(): (
  dispatch: Dispatch,
  getState: GetState
) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.FETCH_DEFAULT_TIMELINE_EVENTS,
    });
    try {
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      invariant(
        currentUser,
        "Cannot load default timeline events without a logged in user."
      );
      const defaultTimelineEventsRef = firebaseApp
        .database()
        .ref("timelineEvents")
        .orderByChild("createdBy")
        .equalTo(currentUser.uid);
      await defaultTimelineEventsRef.on("value", (defaultTimelineEvents) => {
        dispatch({
          type: TIMELINE_ACTION_TYPES.RECEIVE_DEFAULT_TIMELINE_EVENTS,
          payload: {
            defaultTimelineEvents:
              map(defaultTimelineEvents.toJSON(), (event, key) => ({
                uid: key,
                ...event,
              })) || [],
          },
        });
      });
    } catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function setActiveEvent(
  event: TimelineEvent
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: TIMELINE_ACTION_TYPES.RECEIVE_ACTIVE_EVENT,
      payload: {
        timelineEvent: event,
      },
    });
  };
}



// WEBPACK FOOTER //
// ./src/redux/timeline/actions.js